import { useCallback, useEffect, useState } from 'react'

import { getMatterArtifactVersion } from '@/matter-artifacts/fetch/get-matter-artifact-version'
import { selectMatterArtifactVersionById } from '@/matter-artifacts/store/selectors'
import { MatterArtifactsActions } from '@/matter-artifacts/store/slice'
import type { RootState } from '@/store/store'
import { useAppDispatch, useAppSelector } from '@/store/store-hooks'

/** Loads a matter artifact version's metadata into the store when it isn't already there. */
export function useLoadMatterArtifactVersion(matterArtifactVersionId: string) {
  const dispatch = useAppDispatch()
  const isLoaded = useAppSelector((state: RootState) => selectMatterArtifactVersionById(state, matterArtifactVersionId) != null)
  const [hasError, setHasError] = useState(false)
  const [loadAttempt, setLoadAttempt] = useState(0)

  const retry = useCallback(() => {
    setHasError(false)
    setLoadAttempt((attempt) => attempt + 1)
  }, [])

  useEffect(() => {
    if (isLoaded) return

    let cancelled = false
    setHasError(false)
    void getMatterArtifactVersion(matterArtifactVersionId)
      .then((version) => {
        if (!cancelled) dispatch(MatterArtifactsActions.upsertMatterArtifactVersion(version))
      })
      .catch(() => {
        if (!cancelled) setHasError(true)
      })

    return () => {
      cancelled = true
    }
  }, [dispatch, isLoaded, loadAttempt, matterArtifactVersionId])

  return { isLoaded, hasError, retry }
}
